import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import CandidateProfile from '../components/CandidateProfile';
import { SkeletonProfile } from '../components/Skeletons';
import { Card } from '@/components/ui/card';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SharedAnalysis = () => {
  const { shareId } = useParams();
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchShared = async () => {
      try {
        // Fetch candidate data and OG meta in parallel
        const [candidateResp, metaResp] = await Promise.allSettled([
          axios.get(`${API}/share/${shareId}`, { timeout: 15000 }),
          axios.get(`${API}/share/${shareId}/meta`, { timeout: 10000 }),
        ]);

        if (candidateResp.status === 'fulfilled') {
          setCandidate(candidateResp.value.data);
        } else {
          throw candidateResp.reason;
        }

        // Update document meta tags dynamically for social sharing
        if (metaResp.status === 'fulfilled') {
          const meta = metaResp.value.data;
          document.title = meta.title || 'Tô de Olho';

          const updateMeta = (property, content) => {
            let tag = document.querySelector(`meta[property="${property}"]`) || document.querySelector(`meta[name="${property}"]`);
            if (tag) {
              tag.setAttribute('content', content);
            } else {
              tag = document.createElement('meta');
              tag.setAttribute(property.startsWith('og:') ? 'property' : 'name', property);
              tag.setAttribute('content', content);
              document.head.appendChild(tag);
            }
          };

          updateMeta('og:title', meta.title);
          updateMeta('og:description', meta.description);
          updateMeta('og:image', meta.image);
          updateMeta('og:url', window.location.href);
          updateMeta('twitter:title', meta.title);
          updateMeta('twitter:description', meta.description);
          updateMeta('twitter:image', meta.image);
        }
      } catch (err) {
        setError(err?.response?.status === 404 ? 'Link expirado ou invalido.' : 'Erro ao carregar analise.');
      } finally {
        setLoading(false);
      }
    };
    fetchShared();

    // Cleanup: restore original title on unmount
    return () => { document.title = 'Tô de Olho | Análise de Candidatos Políticos'; };
  }, [shareId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F6F8] p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          <SkeletonProfile />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F4F6F8] flex items-center justify-center p-4">
        <Card className="p-8 max-w-md text-center bg-white border border-[#E2E8F0]">
          <AlertCircle className="w-12 h-12 text-[#C62828] mx-auto mb-4" />
          <h2 className="text-xl font-bold text-[#0F1C2E] mb-2">Analise indisponivel</h2>
          <p className="text-sm text-[#475569] mb-4">{error}</p>
          <Link to="/">
            <Button className="bg-[#1E3A5F] hover:bg-[#152B47] text-white" data-testid="share-back-home">
              Ir para o Tô de Olho
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F6F8]">
      {/* Shared Header */}
      <header className="bg-white border-b border-[#E2E8F0] py-3 px-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img
              src="/logo.png"
              alt="Tô de Olho"
              className="h-8 rounded-md"
            />
          </Link>
          <span className="text-xs text-[#94A3B8] bg-[#F4F6F8] px-3 py-1 rounded-full">Analise publica</span>
        </div>
      </header>
      <div className="max-w-4xl mx-auto p-4 md:p-8">
        <CandidateProfile candidate={candidate} />
      </div>
    </div>
  );
};

export default SharedAnalysis;
